import apiClient from './api';

/**
 * 從 localStorage 取得選擇的學期梯次 ID
 * @returns {number|null} 學期梯次 ID，如果未選擇則返回 null
 */
const getSelectedApplyId = () => {
  const aId = localStorage.getItem('selectedApplyId');
  return aId ? parseInt(aId, 10) : null;
};

const applyStudentService = {
  /**
   * 檢查使用者申請狀態
   * @returns {Promise} 包含申請狀態的 Promise
   */
  async checkUserApplication() {
    try {
      const response = await apiClient.get('/ApplyStudent/CheckUserApplication');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Check user application error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to check application status'
      };
    }
  },

  /**
   * 獲取使用者申請表單詳細資料
   * @returns {Promise} 包含申請表單資料的 Promise
   */
  async getUserApplication() {
    try {
      const response = await apiClient.get('/ApplyStudent/GetUserApplication');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get user application error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get application data'
      };
    }
  },

  /**
   * 儲存/更新申請表單內容
   * @param {Object} applicationData 申請表單資料
   * @returns {Promise} 包含儲存結果的 Promise
   */
  async saveUserApplication(applicationData) {
    try {
      const response = await apiClient.put('/ApplyStudent/SaveUserApplication', applicationData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Save user application error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to save application'
      };
    }
  },

  /**
   * 提交申請表單
   * @returns {Promise} 包含提交結果的 Promise
   */
  async submitUserApplication() {
    try {
      const response = await apiClient.put('/ApplyStudent/SubmitUserApplication');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Submit user application error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to submit application'
      };
    }
  },

  /**
   * 檢查是否有進行中的學期梯次
   * @returns {Promise} 包含檢查結果的 Promise
   */
  async checkActiveApplyPeriod() {
    try {
      const response = await apiClient.get('/Apply/CheckActiveApplyPeriod');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Check active apply period error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to check active apply period'
      };
    }
  },

  /**
   * 取得指定學期梯次的條款
   * @param {number} aId 學期梯次 ID（可選，未提供則從 localStorage 讀取）
   * @returns {Promise} 包含條款資料的 Promise
   */
  async getActiveApplyTerms(aId = null) {
    try {
      const applyId = aId || getSelectedApplyId();
      if (!applyId) {
        return {
          success: false,
          message: 'No apply period selected'
        };
      }

      const response = await apiClient.get('/Apply/GetActiveApplyTerms', {
        params: { aId: applyId }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get active apply terms error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get active apply terms'
      };
    }
  },

  /**
   * 取得可用的財務來源選項
   * @returns {Promise} 包含財務來源選項的 Promise
   */
  async getAvailableFinances() {
    try {
      const response = await apiClient.get('/Finance/GetAvailableFinances');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get available finances error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get financial resources'
      };
    }
  },

  /**
   * 取得可用的訊息來源選項
   * @returns {Promise} 包含訊息來源選項的 Promise
   */
  async getAvailableMessageSources() {
    try {
      const response = await apiClient.get('/MessageSource/GetAvailableMessageSources');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get available message sources error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get message sources'
      };
    }
  },

  /**
   * 取得可用的國家列表
   * @returns {Promise} 包含國家列表的 Promise
   */
  async getAvailableCountries() {
    try {
      const response = await apiClient.get('/Country/GetAvailableCountries');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get available countries error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get countries'
      };
    }
  },

  /**
   * 取得指定學期梯次的可申請系所資訊
   * @param {number} aId 學期梯次 ID（可選，未提供則從 localStorage 讀取）
   * @returns {Promise} 包含系所資訊的 Promise
   */
  async getActiveApplyPrograms(aId = null) {
    try {
      const applyId = aId || getSelectedApplyId();
      if (!applyId) {
        return {
          success: false,
          message: 'No apply period selected'
        };
      }

      const response = await apiClient.get('/Apply/GetActiveApplyPrograms', {
        params: { aId: applyId }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get active apply programs error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get apply programs'
      };
    }
  },

  /**
   * 更新申請系所
   * @param {Array} applyPrograms 申請系所陣列
   * @returns {Promise} 包含更新結果的 Promise
   */
  async updateApplyPrograms(applyPrograms, scholarshipSelections) {
    try {
      const response = await apiClient.put('/ApplyStudent/UpdateApplyPrograms', {
        applyPrograms: applyPrograms,
        scholarshipSelections: scholarshipSelections
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update apply programs error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update apply programs'
      };
    }
  },

  /**
   * 取得指定學期梯次的步驟內容
   * @param {number} step 步驟編號 (1-5)
   * @param {number} aId 學期梯次 ID（可選，未提供則從 localStorage 讀取）
   * @returns {Promise} 包含步驟內容的 Promise
   */
  async getActiveApplyStepContent(step, aId = null) {
    try {
      const applyId = aId || getSelectedApplyId();
      if (!applyId) {
        return {
          success: false,
          message: 'No apply period selected',
          data: {
            step: step,
            title: `Step ${step}`,
            content: '',
            applyInfo: null
          }
        };
      }

      const response = await apiClient.get('/Apply/GetActiveApplyStepContent', {
        params: { step, aId: applyId }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get active apply step content error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get step content',
        // 返回預設值
        data: {
          step: step,
          title: `Step ${step}`,
          content: '',
          applyInfo: null
        }
      };
    }
  },

  // ========== 推薦人相關 API ==========

  /**
   * 新增推薦人
   * @param {Object} recommenderData 推薦人資料
   * @param {number} recommenderData.asId 申請表ID
   * @param {string} recommenderData.name 推薦人姓名
   * @param {string} recommenderData.jobTitle 推薦人職務
   * @param {string} recommenderData.schoolOrInstitute 推薦人來源
   * @param {string} recommenderData.email 推薦人信箱
   * @returns {Promise} 包含新增結果的 Promise
   */
  async addRecommender(recommenderData) {
    try {
      const response = await apiClient.post('/ApplyStudent/AddRecommender', recommenderData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Add recommender error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add recommender'
      };
    }
  },

  /**
   * 刪除推薦人
   * @param {number} asrId 推薦人ID
   * @returns {Promise} 包含刪除結果的 Promise
   */
  async deleteRecommender(asrId) {
    try {
      const response = await apiClient.delete(`/ApplyStudent/DeleteRecommender/${asrId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Delete recommender error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete recommender'
      };
    }
  },

  /**
   * 取得所有推薦人
   * @param {number} asId 申請表ID
   * @returns {Promise} 包含推薦人列表的 Promise
   */
  async getRecommenders(asId) {
    try {
      const response = await apiClient.get(`/ApplyStudent/GetRecommenders/${asId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get recommenders error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get recommenders'
      };
    }
  },

  // ========== 推薦信相關 API ==========

  /**
   * 在指定學程上新增推薦信邀請
   * @param {Object} invitationData 推薦信邀請資料
   * @param {number} invitationData.asId 申請表ID
   * @param {number} invitationData.pId 學程ID
   * @param {number} invitationData.asrId 推薦人ID
   * @returns {Promise} 包含新增結果的 Promise
   */
  async addRecommendationLetterInvitation(invitationData) {
    try {
      const response = await apiClient.post('/ApplyStudent/AddRecommendationLetterInvitation', invitationData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Add recommendation letter invitation error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add recommendation letter invitation'
      };
    }
  },

  /**
   * 發送推薦信邀請通知信
   * @param {number} asrlId 推薦信邀請ID
   * @returns {Promise} 包含發送結果的 Promise
   */
  async sendRecommendationLetterInvitation(asrlId) {
    try {
      const response = await apiClient.post(`/ApplyStudent/SendRecommendationLetterInvitation/${asrlId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Send recommendation letter invitation error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send invitation'
      };
    }
  },

  /**
   * 刪除推薦信邀請
   * @param {number} asrlId 推薦信邀請ID
   * @returns {Promise} 包含刪除結果的 Promise
   */
  async deleteRecommendationLetterInvitation(asrlId) {
    try {
      const response = await apiClient.delete(`/ApplyStudent/DeleteRecommendationLetterInvitation/${asrlId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Delete recommendation letter invitation error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete invitation'
      };
    }
  },

  /**
   * 取得某申請表的所有推薦信邀請
   * @param {number} asId 申請表ID
   * @returns {Promise} 包含推薦信邀請列表的 Promise
   */
  async getRecommendationLetters(asId) {
    try {
      const response = await apiClient.get(`/ApplyStudent/GetRecommendationLetters/${asId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get recommendation letters error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get recommendation letters'
      };
    }
  },

  // ========== 檔案上傳相關 API ==========

  /**
   * 取得通用上傳文件資訊
   * @param {number} asId 申請表ID
   * @returns {Promise} 包含通用文件列表的 Promise
   */
  async getGeneralUploadFiles(asId) {
    try {
      const response = await apiClient.get(`/ApplyStudent/GetGeneralUploadFiles/${asId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get general upload files error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get general upload files'
      };
    }
  },

  /**
   * 取得系所上傳文件資訊
   * @param {Object} params 參數
   * @param {number} params.asId 申請表ID
   * @param {number} params.pId 學程ID
   * @returns {Promise} 包含系所文件列表的 Promise
   */
  async getDepartmentUploadFiles(params) {
    try {
      const response = await apiClient.post('/ApplyStudent/GetDepartmentUploadFiles', params);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get department upload files error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get department upload files'
      };
    }
  },

  /**
   * 上傳檔案
   * @param {Object} uploadData 上傳資料
   * @param {number} uploadData.asId 申請表ID
   * @param {Array} uploadData.files 檔案陣列
   * @param {number} uploadData.files[].pId 學程ID（通用文件填0）
   * @param {number} uploadData.files[].afId 文件類型ID
   * @param {string} uploadData.files[].fileName 檔案名稱
   * @param {string} uploadData.files[].base64Data Base64編碼的檔案內容
   * @returns {Promise} 包含上傳結果的 Promise
   */
  async uploadFiles(uploadData) {
    try {
      const response = await apiClient.post('/ApplyStudent/UploadFiles', uploadData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Upload files error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload files'
      };
    }
  },

  /**
   * 上傳大頭照
   * @param {Object} photoData 照片資料
   * @param {number} photoData.asId 申請表ID
   * @param {string} photoData.base64Data Base64編碼的圖片資料，格式：data:image/{type};base64,{base64string}
   * @returns {Promise} 包含上傳結果的 Promise，成功時包含 photo 路徑和 photoPreviewUrl
   */
  async uploadPhoto(photoData) {
    try {
      const response = await apiClient.post('/ApplyStudent/UploadPhoto', photoData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Upload photo error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload photo'
      };
    }
  },

  /**
   * 檢查指定學期梯次是否有資格審核的步驟
   * @param {number} aId 學期梯次 ID（可選，未提供則從 localStorage 讀取）
   * @returns {Promise} 包含檢查結果的 Promise
   */
  async hasActiveApplyEligibility(aId = null) {
    try {
      const applyId = aId || getSelectedApplyId();
      if (!applyId) {
        return {
          success: false,
          message: 'No apply period selected'
        };
      }

      const response = await apiClient.get('/Apply/HasActiveApplyEligibility', {
        params: { aId: applyId }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Check active apply eligibility error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to check eligibility'
      };
    }
  },

  /**
   * 取得指定學期梯次的資格審核資料
   * @param {number} aId 學期梯次 ID（可選，未提供則從 localStorage 讀取）
   * @returns {Promise} 包含資格審核資料的 Promise
   */
  async getActiveApplyEligibility(aId = null) {
    try {
      const applyId = aId || getSelectedApplyId();
      if (!applyId) {
        return {
          success: false,
          message: 'No apply period selected'
        };
      }

      const response = await apiClient.get('/Apply/GetActiveApplyEligibility', {
        params: { aId: applyId }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get active apply eligibility error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get eligibility data'
      };
    }
  },
  
  /**
   * 取得指定學期梯次的繳費說明
   * @param {number} aId 學期梯次 ID（可選，未提供則從 localStorage 讀取）
   * @returns {Promise} 包含繳費說明的 Promise
   */
  async getActiveApplyPayNotes(aId = null) {
    try {
      const applyId = aId || getSelectedApplyId();
      if (!applyId) {
        return {
          success: false,
          message: 'No apply period selected'
        };
      }

      const response = await apiClient.get('/Apply/GetActiveApplyPayNotes', {
        params: { aId: applyId }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get active apply pay notes error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get pay notes data'
      };
    }
  },
  
  /**
   * 取得指定學期梯次的繳費方式
   * @param {number} aId 學期梯次 ID（可選，未提供則從 localStorage 讀取）
   * @returns {Promise} 包含繳費方式的 Promise
   */
  async getActiveApplyPayMethod(aId = null) {
    try {
      const applyId = aId || getSelectedApplyId();
      if (!applyId) {
        return {
          success: false,
          message: 'No apply period selected'
        };
      }

      const response = await apiClient.get('/Apply/GetActiveApplyPayMethod', {
        params: { aId: applyId }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get active apply pay method error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get pay method data'
      };
    }
  },
  
  async getCountryInfo(countryCode) {
    try {
      const response = await apiClient.get('/Country/GetCountryInfo?countryCode=' + countryCode);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get country info error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get country info data'
      };
    }
  },

  /**
   * 取得使用者的申請單訂單資訊
   * @param {number} asId 申請單流水號
   * @returns {Promise} 包含訂單資訊的 Promise
   */
  async getUserOrder(asId) {
    try {
      const response = await apiClient.get(`/ApplyStudent/GetUserOrder/${asId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get user order error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get order information'
      };
    }
  },
  
  async hasUserOrder(asId) {
    try {
      const response = await apiClient.get(`/ApplyStudent/HasUserOrder/${asId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get user order error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get order information'
      };
    }
  },

  /**
   * 檢查必要文件是否已全部上傳完成
   * @param {number} asId 申請表ID
   * @returns {Promise} 包含檢查結果的 Promise，包含 generalComplete 和 departmentComplete 以及缺少的文件列表
   */
  async checkRequiredDocuments(asId) {
    try {
      // 檢查通用文件
      const generalResult = await this.getGeneralUploadFiles(asId);
      const generalComplete = {
        isComplete: true,
        missingFiles: []
      };

      if (generalResult.success && generalResult.data) {
        console.log('General upload files result:', generalResult);
        generalResult.data.forEach(file => {
          if (file.isRequired && (!file.uploadedFile || file.uploadedFile.length === 0)) {
            generalComplete.isComplete = false;
            generalComplete.missingFiles.push({
              name: file.name_EN || file.name,
              type: 'general'
            });
          }
        });
      }

      // 檢查系所文件 - 需要取得所有申請的系所
      const applicationResult = await this.getUserApplication();
      const departmentComplete = {
        isComplete: true,
        missingFiles: []
      };

      if (applicationResult.success && applicationResult.data && applicationResult.data.applyPrograms) {
        const programIds = JSON.parse(applicationResult.data.applyPrograms);
        
        for (const pId of programIds) {
          const deptResult = await this.getDepartmentUploadFiles({ asId, pId });
          
          if (deptResult.success && deptResult.data) {
            deptResult.data.forEach(file => {
              if (file.isRequired && (!file.uploadedFile || file.uploadedFile.length === 0)) {
                departmentComplete.isComplete = false;
                departmentComplete.missingFiles.push({
                  name: file.name_EN || file.name,
                  programId: pId,
                  type: 'department'
                });
              }
            });
          }
        }
      }

      return {
        success: true,
        data: {
          generalComplete: generalComplete.isComplete,
          departmentComplete: departmentComplete.isComplete,
          allComplete: generalComplete.isComplete && departmentComplete.isComplete,
          missingGeneralFiles: generalComplete.missingFiles,
          missingDepartmentFiles: departmentComplete.missingFiles
        }
      };
    } catch (error) {
      console.error('Check required documents error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to check required documents'
      };
    }
  },

  /**
   * 上傳付款證明
   * @param {Object} proofData 付款證明資料
   * @param {number} proofData.asId 申請表ID
   * @param {string} proofData.orderId 訂單ID
   * @param {string} proofData.fileName 檔案名稱
   * @param {string} proofData.base64Data Base64編碼的檔案資料
   * @returns {Promise} 包含上傳結果的 Promise
   */
  async uploadPaymentProof(proofData) {
    try {
      const response = await apiClient.post('/ApplyStudent/UploadPaymentProof', proofData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Upload payment proof error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload payment proof'
      };
    }
  },

  /**
   * 上傳已簽名的申請表
   * @param {Object} formData 申請表資料
   * @param {number} formData.asId 申請表ID
   * @param {string} formData.orderId 訂單ID
   * @param {string} formData.fileName 檔案名稱
   * @param {string} formData.base64Data Base64編碼的檔案資料
   * @returns {Promise} 包含上傳結果的 Promise
   */
  async uploadSignedApplicationForm(formData) {
    try {
      const response = await apiClient.post('/ApplyStudent/UploadSignedApplicationForm', formData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Upload signed application form error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload signed application form'
      };
    }
  },

  /**
   * 根據國籍取得可申請的獎學金清單
   * @param {string} permCountry 學生的國籍代碼
   * @returns {Promise} 包含獎學金清單的 Promise
   */
  async getAvailableScholarshipsByCountry(permCountry = '') {
    try {
      const response = await apiClient.get('/Scholarship/GetAvailableScholarshipsByCountry', {
        params: { permCountry }
      });
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Get available scholarships by country error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get available scholarships',
        data: [],
        count: 0
      };
    }
  },

  /**
   * 根據國籍和學程取得可申請的獎學金清單（有限制學程的獎學金）
   * @param {string} permCountry 學生的國籍代碼
   * @param {Array|number} pIds 學程 ID 或學程 ID 陣列
   * @returns {Promise} 包含獎學金清單的 Promise
   */
  async getAvailableScholarshipsByProgram(permCountry = '', pIds) {
    try {
      // 確保 pIds 是陣列格式
      const programIds = Array.isArray(pIds) ? pIds : [pIds];
      
      const response = await apiClient.get('/Scholarship/GetAvailableScholarshipsByProgram', {
        params: { 
          permCountry, 
          PIds: JSON.stringify(programIds)
        }
      });
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Get available scholarships by program error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get available scholarships by program',
        data: [],
        count: 0
      };
    }
  }
};

export default applyStudentService;
